import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Star, Settings, LayoutGrid, ShoppingBag, LogOut, Sparkles } from 'lucide-react';
import { Button } from '@mui/material';
import CategoryManager from './admin/CategoryManager';
import ProductManager from './admin/ProductManager';
import OrderManager from './admin/OrderManager';
import ReviewManager from './admin/ReviewManager';
import WeeklyDealsManager from './admin/WeeklyDealsManager';
import SettingsManager from './admin/SettingsManager';
import { useAuthStore } from '../firebase/auth';

import StarryBackground from './StarryBackground';

const AdminPanel = () => {
  const [activeSection, setActiveSection] = useState<'products' | 'categories' | 'orders' | 'reviews' | 'weeklyDeals' | 'settings'>('categories');
  const navigate = useNavigate();
  const signOut = useAuthStore((state) => state.signOut);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const navItems = [
    { id: 'categories', label: 'Categorías', icon: LayoutGrid },
    { id: 'products', label: 'Productos', icon: Package },
    { id: 'orders', label: 'Pedidos', icon: ShoppingBag },
    { id: 'reviews', label: 'Reseñas', icon: Star },
    { id: 'weeklyDeals', label: 'Ofertas Semanales', icon: Sparkles },
    { id: 'settings', label: 'Configuración', icon: Settings },
  ];

  return (
    <StarryBackground>
      <div className="min-h-screen font-rajdhani">
        <header className="bg-black/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-3xl font-orbitron font-bold bg-clip-text text-transparent bg-gradient-to-r from-neon-green to-neon-blue tracking-tight">
              PANEL DE CONTROL
            </h1>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-neon-blue hover:text-white transition-colors font-semibold flex items-center gap-2">
                <LayoutGrid className="w-4 h-4" />
                Volver al sitio
              </Link>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-500/50 text-red-500 hover:bg-red-500/10 transition-all"
              >
                <LogOut className="h-4 w-4" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex space-x-4 mb-8 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-neon-blue/20 scrollbar-track-transparent">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id as any)}
                className={`
                  flex items-center px-6 py-3 rounded-xl transition-all duration-300 whitespace-nowrap
                  ${activeSection === item.id
                    ? 'bg-neon-green/10 text-neon-green border border-neon-green shadow-[0_0_15px_rgba(0,255,0,0.2)]'
                    : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/30 hover:text-white'
                  }
                `}
              >
                <item.icon className={`h-5 w-5 mr-3 ${activeSection === item.id ? 'animate-pulse' : ''}`} />
                <span className="font-bold tracking-wide">{item.label}</span>
              </button>
            ))}
          </div>

          <div className="bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 min-h-[600px]">
            {activeSection === 'categories' && <CategoryManager />}
            {activeSection === 'products' && <ProductManager />}
            {activeSection === 'orders' && <OrderManager />}
            {activeSection === 'reviews' && <ReviewManager />}
            {activeSection === 'weeklyDeals' && <WeeklyDealsManager />}
            {activeSection === 'settings' && <SettingsManager />}
          </div>
        </div>
      </div>
    </StarryBackground>
  );
};

export default AdminPanel;